import React, { useState, useEffect } from 'react';
import { Home, Menu } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface HomeButtonProps {
  onClick?: () => void;
  className?: string;
  variant?: 'home' | 'menu';
  showLabel?: boolean;
}

export default function HomeButton({ 
  onClick, 
  className, 
  variant = 'home',
  showLabel = false 
}: HomeButtonProps) {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get responsive sizes
  const getButtonSize = () => {
    if (isMobile) return { width: 36, height: 36 };
    return { width: 42, height: 42 };
  };

  const getIconContainerSize = () => {
    if (isMobile) return { width: 22, height: 22 };
    return { width: 26, height: 26 };
  };

  const getIconSize = () => {
    if (isMobile) return 16;
    return 18;
  };

  const getPosition = () => {
    if (isMobile) return 'top-[20px] left-[65px]';
    return 'top-[25px] left-[75px]';
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (variant === 'home') {
      router.push('/');
    }
  };

  const IconComponent = variant === 'home' ? Home : Menu;
  const label = variant === 'home' ? 'Trang chủ' : 'Menu';
  const buttonSize = getButtonSize();
  const iconContainerSize = getIconContainerSize();
  return (
    <button
      id={`nav_${variant}_button`}
      className={`
        fixed ${getPosition()} z-[19999]
        flex items-center gap-2 px-3 py-2 
        bg-white/90 backdrop-blur-[20px] 
        rounded-full border border-white/20
        shadow-[0_4px_16px_rgba(0,0,0,0.15),inset_-1px_1px_3px_rgba(255,255,255,0.8),inset_0px_4px_10px_3px_rgba(255,255,255,0.2)]
        hover:bg-white/95 hover:scale-105 
        active:scale-95 active:bg-white/80
        transition-all duration-300 ease-out
        cursor-pointer
        ${isHovered && showLabel && !isMobile ? 'w-auto pr-4' : `w-[${buttonSize.width}px]`}
        ${className || ''}
      `}
      style={{
        height: `${buttonSize.height}px`
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={label}
      type="button"
    >
      <div 
        className="flex items-center justify-center"
        style={{
          width: `${iconContainerSize.width}px`,
          height: `${iconContainerSize.height}px`
        }}
      >
        <IconComponent 
          size={getIconSize()} 
          className="text-gray-700 hover:text-blue-600 transition-colors duration-200" 
        />
      </div>
      
      {/* Animated label - only show on desktop */}
      {showLabel && !isMobile && (
        <span 
          className={`
            ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 whitespace-nowrap
            transition-all duration-300 ease-out
            ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 w-0'}
          `}
        >
          {label}
        </span>
      )}
    </button>
  );
}
